import { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, AlertTriangle, Clock, Code, CheckCircle } from 'lucide-react';
import edgeService from '../../services/edge.service';
import { useToast } from '../common/ToastContainer';
import RawJsonViewer from '../common/RawJsonViewer';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';
import { useAuthStore } from '../../stores/authStore';
import { USER_TYPES } from '../../utils/constants';

interface ConfigConfirmationProps {
  edgeId: string;
  // VSNP-700 follow-up: when true, surfaces a compact amber banner at the
  // top of the panel making it unmissable that the data below is the last
  // confirmed snapshot from before the edge went offline (and any changes
  // saved on the Config page won't reach VPP until reconnect).
  isOffline?: boolean;
}

interface StatusData {
  timestamp?: number;
  rid?: number;
  edge_id?: string;
  version?: string;
  vpp?: {
    version?: string;
    uptime_seconds?: number;
    interfaces?: any[];
    routes?: any[];
    nat?: any;
    dhcp?: any;
  };
  data?: {
    hw_ver?: string;
    sw_ver?: string;
    man_inf?: string;
    man_ser_num?: string;
    up_time?: string;
    up_type?: number;
  };
  _metadata?: {
    id: number;
    edge_id: string;
    received_at: string;
    trigger_source: string;
  };
}

const ConfigConfirmation = ({ edgeId, isOffline = false }: ConfigConfirmationProps) => {
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  // VSNP-960: the raw JSON dump is a low-level diagnostic surface — Super Admin
  // only. Concrete type check (Channel Admin type 5 excluded), consistent with
  // the other diagnostic gates on this page.
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;
  const [statusData, setStatusData] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);
  const toast = useToast();

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await edgeService.getLatestStatus(edgeId);
      setStatusData(data);
    } catch (err: any) {
      console.error('Failed to load config confirmation:', err);
      if (err.message.includes('No status data found')) {
        setStatusData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const requestStatus = async () => {
    try {
      setRequesting(true);
      await edgeService.requestStatus(edgeId);
      toast.success('Status Request Sent', 'Edge device will publish status shortly. Refresh in a few seconds.');
    } catch (err: any) {
      toast.error('Request Failed', err.message || 'Failed to request status from edge');
    } finally {
      setRequesting(false);
    }
  };

  useEffect(() => {
    if (edgeId) {
      fetchStatus();
    }
  }, [edgeId]);

  const formatUptime = (uptimeMs?: string | number) => {
    if (!uptimeMs) return '-';
    const seconds = typeof uptimeMs === 'string' ? parseInt(uptimeMs) / 1000 : uptimeMs;
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  // Detect format type (regardless of data presence)
  const isVPPFormat = statusData?.vpp !== undefined;
  const isLegacyFormat = !isVPPFormat && statusData?.data !== undefined;

  // Check if VPP data is populated
  const hasVPPData = isVPPFormat && (
    (statusData?.vpp?.interfaces && statusData.vpp.interfaces.length > 0) ||
    (statusData?.vpp?.routes && statusData.vpp.routes.length > 0)
  );

  return (
    <div className="card space-y-4">
      {/* VSNP-700 follow-up: prominent-but-compact offline notice so
          operators reading this panel know the snapshot below is from
          before the edge went offline. */}
      {isOffline && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden="true" />
          <span>
            <span className="font-semibold">Edge offline.</span>{' '}
            The snapshot below is the last confirmation received before the edge disconnected.
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center space-x-3">
          <Clock className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Configuration Confirmation Status</h3>
        </div>
        <div className="flex items-center space-x-2">
          {/* VSNP-960: Raw JSON toggle is Super-Admin-only. */}
          {isSuperAdmin && (
            <button
              onClick={() => setShowRawJson(!showRawJson)}
              className="btn-secondary inline-flex items-center space-x-2"
            >
              <Code className="w-4 h-4" />
              <span>{showRawJson ? 'Hide' : 'Show'} Raw JSON</span>
            </button>
          )}
          <button
            onClick={requestStatus}
            disabled={requesting}
            className="btn-secondary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${requesting ? 'animate-spin' : ''}`} />
            <span>Request Status</span>
          </button>
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="btn-secondary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Last Update Info */}
      {statusData?._metadata && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-sm text-gray-600">Last Confirmation</div>
            <div className="text-lg font-semibold text-gray-900">
              {formatDate(statusData._metadata.received_at, dateSettings)}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Trigger Source</div>
            <div className="text-lg font-semibold text-gray-900 capitalize">
              {statusData._metadata.trigger_source.replace(/_/g, ' ')}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Format</div>
            <div className="text-lg font-semibold">
              {hasVPPData ? (
                <span className="text-green-600">VPP Format (Data Present)</span>
              ) : isVPPFormat ? (
                <span className="text-blue-600">VPP Format (Empty)</span>
              ) : isLegacyFormat ? (
                <span className="text-yellow-600">Legacy Format</span>
              ) : (
                <span className="text-gray-600">Unknown</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Type Indicator */}
      {statusData && (
        <div className="space-y-3">
          {hasVPPData && (
            <div className="flex items-center space-x-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
              <CheckCircle className="w-4 h-4" />
              <span>
                VPP status data available ({statusData.vpp?.interfaces?.length || 0} interfaces, {statusData.vpp?.routes?.length || 0} routes)
              </span>
            </div>
          )}

          {statusData.data && (
            <div className="flex items-start space-x-2 text-sm text-yellow-700 bg-yellow-50 px-3 py-2 rounded-lg">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <div>
                <div className="font-medium">Legacy Startup Information</div>
                <div className="text-xs mt-1 space-y-1">
                  <div>Hardware: {statusData.data.hw_ver}</div>
                  <div>Software: {statusData.data.sw_ver}</div>
                  <div>Manufacturer: {statusData.data.man_inf} ({statusData.data.man_ser_num})</div>
                  <div>Uptime: {formatUptime(statusData.data.up_time)}</div>
                </div>
                <div className="mt-2 text-xs">
                  <strong>Note:</strong> This edge is using the legacy status format. VPP interface and route data may not be available for drift detection. Consider updating the edge agent to enable full VPP status collection.
                </div>
              </div>
            </div>
          )}

          {isVPPFormat && !hasVPPData && (
            <div className="flex items-center space-x-2 text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-lg">
              <AlertCircle className="w-4 h-4" />
              <span>VPP format detected but interfaces/routes arrays are empty. VPP may be initializing or Binary API collection may be failing.</span>
            </div>
          )}

          {!isVPPFormat && !isLegacyFormat && (
            <div className="flex items-center space-x-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
              <AlertCircle className="w-4 h-4" />
              <span>No recognizable status data in confirmation payload</span>
            </div>
          )}
        </div>
      )}

      {/* Raw JSON View — VSNP-960: Super-Admin-only, matching the toggle gate. */}
      {isSuperAdmin && showRawJson && statusData && (
        <RawJsonViewer data={statusData} className="border-t pt-4" titleSize="sm" />
      )}

      {/* No Data State */}
      {!loading && !statusData && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Configuration Confirmations Available</h3>
          <p className="text-gray-600 mb-4">
            No status confirmations have been received from this edge device yet.
          </p>
          <button
            onClick={requestStatus}
            disabled={requesting}
            className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${requesting ? 'animate-spin' : ''}`} />
            <span>Request Status Now</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ConfigConfirmation;
